import React from 'react';
import useSWR from 'swr';
import HouseholdRelationLabels from '@/constants/constituents/HouseholdRelationLabels';
import RelationStatus from '@/constants/constituents/RelationStatus';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import fetcher from '@/lib/swr/fetcher';
import {
  convertConstituentType as convertConstituentTypeService,
  createConstituent as createConstituentService,
  removeConnection as removeConnectionService,
  updateConstituent as updateConstituentService,
} from '@/services/constituentServices';
import {
  createRelationship as createRelationshipService,
  deleteRelationship as deleteRelationshipService,
  updateRelationship as updateRelationshipService,
} from '@/services/relationships';
import showErrorMessage from '@/utils/toasts/showErrorMessage';

/**
 * Make sure that we send appropriate relationship pairs. e.g.
 *
 * - if we're creating a parent relationship, the other side of the relationship should be labeled "child"
 *   - (and vice versa)
 * - if we mark a relation as deceased, only one side of the relation should say "deceased"
 */
function getParentAndRelatedRelationData(relationshipData) {
  const data = {
    parentRelationLabel: relationshipData.relationLabel,
    relatedRelationLabel: relationshipData.relationLabel,
    parentRelationStatus: relationshipData.relationStatus,
    relatedRelationStatus: relationshipData.relationStatus,
    relationLabel: null,
    relationStatus: null,
  };

  switch (data.parentRelationLabel) {
    case HouseholdRelationLabels.PARENT:
      data.relatedRelationLabel = HouseholdRelationLabels.CHILD;
      break;
    case HouseholdRelationLabels.CHILD:
      data.relatedRelationLabel = HouseholdRelationLabels.PARENT;
      break;
    default:
      break;
  }

  if (data.parentRelationStatus === RelationStatus.DECEASED) {
    delete data.relatedRelationStatus;
  }

  return data;
}

const useConstituent = (constituentId, swrOptions) => {
  const [isBusy, setIsBusy] = React.useState(false);
  const [connectionsUpdating, setConnectionsUpdating] = React.useState([]);
  const ownerOrgId = useOwnerOrgId();
  const key = constituentId
    ? `orgs/${ownerOrgId}/constituents/${constituentId}`
    : null;
  const { data, error, mutate, isLoading, isValidating } = useSWR(
    key,
    fetcher,
    swrOptions,
  );

  const createRelationship = React.useCallback(
    async ({ relationName, constituentData, relationshipData }) => {
      if (!key || isBusy) {
        return;
      }

      let { relatedId } = relationshipData;

      if (constituentData) {
        const {
          data: { constituent },
        } = await createConstituentService({
          apiType: relationName,
          constituentId,
          data: constituentData,
          ownerOrgId,
        });

        relatedId = constituent.constituentId;
      }

      await createRelationshipService({
        relationName,
        ownerOrgId,
        data: {
          ...relationshipData,
          ...getParentAndRelatedRelationData(relationshipData),
          relationName,
          relatedId,
          resourceEnum: 'cnx',
          parentId: constituentId,
        },
      });

      await mutate();
    },
    [key, isBusy, mutate],
  );

  const updateRelationship = React.useCallback(
    async (relationshipId, relationshipData) => {
      if (!key || !data.constituent || isBusy) {
        return;
      }

      const patch = {
        ...relationshipData,
        ...getParentAndRelatedRelationData(relationshipData),

        // in order to update the related label & status, we have to pass the same ID for both parent and related
        // https://instilio.slack.com/archives/C021Z7RJJ11/p1647367779982169?thread_ts=1647358090.956089&cid=C021Z7RJJ11
        parentId: data.constituent.constituentId,
        relatedId: data.constituent.constituentId,
      };

      await updateRelationshipService({
        ownerOrgId,
        relationshipId,
        data: patch,
      });

      await mutate();
    },
    [key, isBusy, mutate, data],
  );

  const removeRelationship = React.useCallback(
    async (relationshipId) => {
      if (!key || isBusy) {
        return;
      }

      try {
        await deleteRelationshipService({
          ownerOrgId,
          relationshipId,
        });

        await mutate();
      } catch (e) {
        showErrorMessage({
          message: translate('API_ERROR_UNKNOWN'),
        });
      }
    },
    [key, isBusy, mutate, data],
  );

  const createConnection = React.useCallback(
    async ({ apiType, data: connectionData }) => {
      if (!key || isBusy) return;
      setIsBusy(true);
      try {
        await createConstituentService({
          apiType,
          constituentId,
          data: connectionData,
          ownerOrgId,
        });
        await mutate();
      } catch (err) {
        showErrorMessage({
          message: translate('CONSTITUENT_CREATE_CONNECTION_ERROR_MESSAGE', {
            name: connectionData.displayName,
          }),
        });
      }
      setIsBusy(false);
    },
    [isBusy, key, mutate],
  );

  const removeConnection = React.useCallback(
    async ({ apiType, connectionId }) => {
      if (!key || connectionsUpdating.includes(connectionId)) return;
      setConnectionsUpdating((prevState) => [...prevState, connectionId]);
      try {
        await removeConnectionService({
          apiType,
          constituentId,
          connectionId,
          ownerOrgId,
        });
        await mutate();
      } catch (err) {
        showErrorMessage({
          message: translate('CONSTITUENT_REMOVE_CONNECTION_ERROR_MESSAGE', {
            name: data.constituent.displayName,
          }),
        });
      }
      setConnectionsUpdating((prevState) =>
        prevState.filter((id) => id !== connectionId),
      );
    },
    [data, isBusy, key, mutate],
  );

  const updateConnection = React.useCallback(
    async ({ apiType, data: connectionData }) => {
      if (!key || connectionsUpdating.includes(connectionData.constituentId))
        return;
      setConnectionsUpdating((prevState) => [
        ...prevState,
        connectionData.constituentId,
      ]);
      try {
        await updateConstituentService({
          apiType,
          connectionId: connectionData.constituentId,
          constituentId,
          data: connectionData,
          ownerOrgId,
        });
        await mutate();
      } catch (err) {
        showErrorMessage({
          message: translate('CONSTITUENT_CREATE_UPDATING_ERROR_MESSAGE', {
            name: connectionData.displayName,
          }),
        });
      }
      setConnectionsUpdating((prevState) =>
        prevState.filter((id) => id !== connectionData.constituentId),
      );
    },
    [isBusy, key, mutate],
  );

  const updateConstituent = React.useCallback(
    async (updates) => {
      if (!key || isBusy) return;
      setIsBusy(true);
      try {
        const res = await updateConstituentService({
          ownerOrgId,
          constituentId,
          data: updates,
        });
        mutate({ constituent: res.data.constituent }, { revalidate: false });
      } catch (err) {
        showErrorMessage({
          message: translate('UPDATE_CONSTITUENT_ERROR_MESSAGE', {
            name: data.displayName,
          }),
        });
        throw err;
      }
      setIsBusy(false);
    },
    [isBusy, key, mutate],
  );

  const convertConstituentType = React.useCallback(
    async (constituentType) => {
      if (!key || isBusy) return;
      setIsBusy(true);
      try {
        mutate(
          (currentData) => ({
            constituent: { ...currentData, constituentType },
          }),
          {
            revalidate: false,
          },
        );
        const res = await convertConstituentTypeService({
          constituentType,
          constituentId,
          ownerOrgId,
        });
        mutate({ constituent: res.data.constituent }, { revalidate: false });
      } catch (err) {
        showErrorMessage({
          message: translate('UPDATE_CONSTITUENT_ERROR_MESSAGE', {
            name: data.displayName,
          }),
        });
      }
      setIsBusy(false);
    },
    [isBusy, key, mutate],
  );
  return {
    connectionsUpdating,
    convertConstituentType,
    createConnection,
    data: data?.constituent,
    error,
    isBusy,
    isLoading,
    isValidating,
    lockedAttributes: data?.lockedAttributes,
    removeConnection,
    updateConnection,
    updateConstituent,
    createRelationship,
    updateRelationship,
    removeRelationship,
  };
};

export default useConstituent;
